import { action, makeObservable, observable, runInAction } from 'mobx';
import StatusRevisaoPautaApi, { IStatusRevisaoResponse } from '../api/StatusRevisaoPautaApi';
import { corPadraoStatusRevisao, IStatusRevisao, normalizarStatusRevisao, statusRevisaoPorCodigo } from '../modelo/StatusRevisao';
import UsuarioStore from './UsuarioStore';

class StatusRevisaoPautaStore {
    status = observable.array<IStatusRevisao>([]);
    carregando = false;
    gabineteCarregado: string | undefined;

    constructor() {
        makeObservable(this, {
            status: observable,
            carregando: observable,
            gabineteCarregado: observable,
            carregar: action,
            criarStatus: action,
            criarPadrao: action,
            atualizarStatus: action,
            excluirStatus: action,
        });
    }

    get lista(): IStatusRevisao[] {
        return this.status;
    }

    get carregadoParaGabineteAtual(): boolean {
        const gabinete = UsuarioStore.gabinete || '';
        return !!gabinete && this.gabineteCarregado === gabinete;
    }

    private toListaNormalizada(lista: Array<{ numero: number; codigoMagistrado?: string; descricao: string; cor?: string }>): IStatusRevisao[] {
        return lista
            .map(item => normalizarStatusRevisao(item))
            .filter((item): item is IStatusRevisao => !!item);
    }

    private setLista(lista: IStatusRevisao[], gabinete: string) {
        this.status.replace(lista);
        this.gabineteCarregado = gabinete;
    }

    private async preencherCoresPadraoAusentes(
        gabinete: string,
        lista: IStatusRevisaoResponse[],
    ): Promise<IStatusRevisaoResponse[]> {
        const statusSemCor = lista.filter(status => !status.cor && corPadraoStatusRevisao(status.descricao));
        if (statusSemCor.length === 0) {
            return lista;
        }

        let atualizouAlgumaCor = false;
        for (const status of statusSemCor) {
            const cor = corPadraoStatusRevisao(status.descricao);
            if (!cor) {
                continue;
            }

            try {
                await StatusRevisaoPautaApi.atualizar(gabinete, status.numero, { cor });
                atualizouAlgumaCor = true;
            } catch (erro) {
                console.error(`Não foi possível definir a cor padrão do status de revisão ${status.numero}.`, erro);
            }
        }

        return atualizouAlgumaCor
            ? StatusRevisaoPautaApi.listarPorGabinete(gabinete)
            : lista;
    }

    async carregar(force = false): Promise<IStatusRevisao[]> {
        const gabinete = UsuarioStore.gabinete;
        if (!gabinete) {
            runInAction(() => {
                this.status.clear();
                this.gabineteCarregado = undefined;
            });
            return this.lista;
        }

        if (!force && this.carregadoParaGabineteAtual) {
            return this.lista;
        }

        runInAction(() => {
            this.carregando = true;
        });

        try {
            let lista;

            try {
                lista = await StatusRevisaoPautaApi.listarPorGabinete(gabinete);
            } catch (_error) {
                // Se o backend ainda não tiver os status do gabinete, tenta criar os padrões.
                lista = await StatusRevisaoPautaApi.criarDefault(gabinete);
            }

            // O backend cria os quatro status padrão quando o gabinete ainda não tem nenhum.
            if (!lista || lista.length === 0) {
                try {
                    lista = await StatusRevisaoPautaApi.criarDefault(gabinete);
                } catch (_error) {
                    // Se outro cliente criou entre o GET e o POST, recarrega a lista.
                    lista = await StatusRevisaoPautaApi.listarPorGabinete(gabinete);
                }
            }

            lista = await this.preencherCoresPadraoAusentes(gabinete, lista || []);
            const normalizada = this.toListaNormalizada(lista || []);
            runInAction(() => {
                this.setLista(normalizada, gabinete);
            });
            return this.lista;
        } catch (error) {
            console.error('Não foi possível carregar os status de revisão de pauta do gabinete.', error);
            runInAction(() => {
                this.setLista([], gabinete);
            });
            return this.lista;
        } finally {
            runInAction(() => {
                this.carregando = false;
            });
        }
    }

    getStatus(numero?: number): IStatusRevisao | undefined {
        return statusRevisaoPorCodigo(numero, this.lista);
    }

    async criarStatus(descricao: string, cor: string): Promise<IStatusRevisao[]> {
        const gabinete = UsuarioStore.gabinete;
        if (!gabinete) {
            return this.lista;
        }

        const statusCriado = await StatusRevisaoPautaApi.criar(gabinete, descricao);
        const numeroStatus = statusCriado[0]?.numero;
        if (numeroStatus !== undefined) {
            await StatusRevisaoPautaApi.atualizar(gabinete, numeroStatus, { cor });
        }
        // O POST pode devolver apenas o status recém-criado. Recarrega a lista do
        // gabinete para não substituir os status existentes no estado local.
        return this.carregar(true);
    }

    async criarPadrao(): Promise<IStatusRevisao[]> {
        const gabinete = UsuarioStore.gabinete;
        if (!gabinete) {
            return this.lista;
        }

        const lista = await StatusRevisaoPautaApi.criarDefault(gabinete);
        const normalizada = this.toListaNormalizada(lista || []);
        runInAction(() => {
            this.setLista(normalizada, gabinete);
        });
        return this.lista;
    }

    async atualizarStatus(numero: number, dados: { descricao?: string; cor?: string }): Promise<IStatusRevisao | undefined> {
        const gabinete = UsuarioStore.gabinete;
        if (!gabinete) {
            return undefined;
        }

        await StatusRevisaoPautaApi.atualizar(gabinete, numero, dados);
        await this.carregar(true);
        return this.getStatus(numero);
    }

    async excluirStatus(numero: number): Promise<IStatusRevisao[]> {
        const gabinete = UsuarioStore.gabinete;
        if (!gabinete) {
            return this.lista;
        }

        await StatusRevisaoPautaApi.excluir(gabinete, numero);
        return this.carregar(true);
    }
}

export default new StatusRevisaoPautaStore();
